import { z } from "zod";
import { mirrorPersistentValue, removePersistentValue } from "../platform";
import { mirrorInWorkerStore } from "./idb";
export const DEVICE_KEYS = ["arabom:area", "arabom:watch", "arabom:recent", "arabom:last-visit", "arabom:alerts"] as const;
export const areaSchema = z.object({ sigunguCode: z.string().regex(/^\d{5}$/), includeNeighbors: z.boolean(), setAt: z.string().datetime() });
export type Area = z.infer<typeof areaSchema>;
export type SavedCase = { id: string; nameMasked: string; targetType?: string; savedAt: string };
export function readDevice<T>(key: string, fallback: T, storage?: Storage): T {
  try { return JSON.parse((storage ?? localStorage).getItem(key) ?? "null") as T ?? fallback; } catch { return fallback; }
}
export function writeDevice(key: string, value: unknown, storage?: Storage): boolean {
  try {
    const raw = JSON.stringify(value); (storage ?? localStorage).setItem(key, raw);
    void mirrorPersistentValue(key, raw).catch(() => undefined);
    void mirrorInWorkerStore(key, value).catch(() => undefined);
    if (typeof window !== "undefined") window.dispatchEvent(new Event("arabom:device-change"));
    return true;
  } catch { return false; }
}
export function removeDevice(key: string, storage?: Storage): boolean {
  try {
    (storage ?? localStorage).removeItem(key);
    void removePersistentValue(key).catch(() => undefined);
    void mirrorInWorkerStore(key, null).catch(() => undefined);
    if (typeof window !== "undefined") window.dispatchEvent(new Event("arabom:device-change"));
    return true;
  } catch { return false; }
}
export function readArea(): Area | null {
  const value = areaSchema.safeParse(readDevice("arabom:area", null));
  return value.success ? value.data : null;
}
export function readSaved(key: "arabom:recent" | "arabom:watch"): SavedCase[] {
  const raw = readDevice<unknown>(key, []);
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((value) => typeof value === "string" ? [{id:value,nameMasked:"이름 비공개",savedAt:""}] : value && typeof value === "object" && typeof value.id === "string" && typeof value.nameMasked === "string" ? [value as SavedCase] : []);
}

// Optional snapshots and per-tab markers must never prevent public information from opening.
function optionalStorage(kind:"localStorage"|"sessionStorage"):Storage {
 const run=<T>(operation:(store:Storage)=>T,fallback:T):T=>{try{return operation(globalThis[kind])}catch{return fallback}};
 return {get length(){return run(s=>s.length,0)},key:index=>run(s=>s.key(index),null),getItem:key=>run(s=>s.getItem(key),null),setItem:(key,value)=>{run(s=>s.setItem(key,value),undefined)},removeItem:key=>{run(s=>s.removeItem(key),undefined)},clear:()=>{run(s=>s.clear(),undefined)}};
}
export const sessionStore=optionalStorage("sessionStorage");
export const snapshotStore=optionalStorage("localStorage");
